import classNames from 'classnames'
import { useState } from 'react'
import { Statsig } from 'src/sdk/statsig'
import { useProductContextSelector } from 'src/components/restructure/product/contexts'
import { makeEventTrack, sendEvent } from 'src/utils/restructure/analytics'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { getInitialItem } from 'src/components/restructure/utils/get-initial-sku'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { DESCRIPTION_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { SsrOnlyContent } from 'src/components/common/SsrOnlyContent/SsrOnlyContent'

import { Shipping } from './Shipping'
import { Description } from './Description'

type ActiveTabState = 'shipping' | 'description'

export function TabbedNavigation() {
  const product = useProductContextSelector((ctx) => ctx.product)
  const description = useProductContextSelector(
    (ctx) => ctx.currentSku.description
  )
  
  const manufacturerCode = useProductContextSelector(
    (ctx) => ctx.currentSku.manufacturerCode
  )

  const { pageData } = useGlobalContext()

  const isDescriptionExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    DESCRIPTION_EXPERIMENT_ID
  )

  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(
    getInitialItem(product.items, product.slug)
  )

  const featureDetailsObject = getFeatureObject({
    locationOnPage: null,
    name: 'Descrição',
    section: 'produto',
  })

  const [activeTab, setActiveTab] = useState<ActiveTabState>('shipping')

  const shippingEventTrack = makeEventTrack({
    eventAction: 'Frete e retirada',
    eventPage: 'pagina de produto',
  })

  const descriptionEventTrack = makeEventTrack({
    eventAction: 'Descrição',
    eventPage: 'pagina de produto',
  })

  return (
    <section className="mt-lg pb-6">
      <ul className="flex items-center gap-lg">
        <li>
          {isDescriptionExperimentEnabled ? (
            <span className="desktop-body-semibold-text2">
              Calcule frete e retirada
            </span>
          ) : (
            <button
              data-testid="pdp-frete-retirada-button"
              onClick={() => {
                sendEvent(shippingEventTrack)
                sendProductInformationSelectedEvent({
                  clickedElement: 'Frete e retirada',
                  featureDetailsObject,
                  productObject,
                  standardObject,
                })
                setActiveTab('shipping')
              }}
              className={classNames('desktop-body-regular-text2 pb-xs', {
                'border-b border-restructure-border-information desktop-body-semibold-text2 text-restructure-action':
                  activeTab === 'shipping',
              })}
            >
              Frete e retirada
            </button>
          )}
        </li>
        {description !== '' && !isDescriptionExperimentEnabled && (
          <li>
            <button
              data-testid="pdp-descricao-button"
              onClick={() => {
                sendProductInformationSelectedEvent({
                  clickedElement: 'Descrição',
                  featureDetailsObject,
                  productObject,
                  standardObject,
                })
                sendEvent(descriptionEventTrack)
                setActiveTab('description')
              }}
              className={classNames('desktop-body-regular-text2 pb-xs', {
                'border-b border-restructure-border-information desktop-body-semibold-text2 text-restructure-action':
                  activeTab === 'description',
              })}
            >
              Descrição
            </button>
          </li>
        )}
      </ul>

      <div className="scroll-bar-gray mt-lg overflow-y-auto">
        <div
          className={classNames('block', {
            hidden: activeTab !== 'shipping',
          })}
        >
          <Shipping />
        </div>
        {!isDescriptionExperimentEnabled && (
          <>
            <div
              className={classNames('block', {
                hidden: activeTab !== 'description',
              })}
            >
              {activeTab === 'description' && (
                <Description
                  productCode={manufacturerCode}
                  description={description}
                />
              )}
            </div>

            {description !== '' && activeTab !== 'description' && (
              <SsrOnlyContent
                sectionName="tabbed-description"
                testId="ssr-only-content-descricao"
              >
                <Description description={description} />
              </SsrOnlyContent>
            )}
          </>
        )}
      </div>
    </section>
  )
}
